import TooltipWrapper from '@/components/base/TooltipWrapper/TooltipWrapper';
import { TREE_ITEM_TOOLTIP_ID } from '@/storybook/components/FormCollection/components/constants';
import LoadingSpinner from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import ScrollArea from '@/storybook/components/ScrollArea/ScrollArea';
import Text from '@/storybook/components/Text/Text';
import { type CSSProperties, PropsWithChildren, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import DropdownActionBar, { DropdownActionBarProps } from '../DropdownActionBar/DropdownActionBar';
import DropdownSearchInput from '../DropdownSearchInput/DropdownSearchInput';
import styles from './DropdownList.module.css';
import { SelectPanelProvider } from './SelectPanelContext';
import { useDropdownFloatingPopper } from './useDropdownFloatingPopper';

const TOOLTIP_STYLE: CSSProperties = {
  zIndex: 'calc(var(--z-above-all) + 1)',
};

export type DropdownListProps = {
  'isOpen': boolean;
  'isLoading'?: boolean;
  /** When true, option rows (e.g. TreeItem) read disabled state from {@link useSelectPanel} instead of prop drilling. */
  'disabled'?: boolean;
  'isSearchable'?: boolean;
  'position': 'up' | 'down';
  'searchValue'?: string;
  'onSearchValueChange'?: (value: string) => void;
  'numberOfSearchResults'?: number;
  'data-testid'?: string;
  'listRef'?: React.RefObject<HTMLElement | null> | null;
  'dropdownTriggerRef'?: React.RefObject<HTMLElement | null>;
  /** When true, the panel is portalled to the document body and positioned with Popper (escaping overflow clipping). Defaults to inline. */
  'isFloating'?: boolean;
  /** Receives the portalled floating panel node so owners can exclude it from their outside-click detection. */
  'panelRef'?: React.RefObject<HTMLElement | null>;
  'optionsCount': number;
  'searchEnabledThreshold'?: number;
  'actionBarProps'?: DropdownActionBarProps;
};

const SAFETY_MARGIN = 100;
const MIN_HEIGHT = 100;
// 28rem at the app's 62.5% root font-size (1rem = 10px); keep in sync with the '28rem' ScrollArea fallback below.
const FLOATING_DEFAULT_MAX_HEIGHT = 280;

export default function DropdownList({
  isOpen,
  searchValue,
  onSearchValueChange,
  isLoading = false,
  'disabled': panelDisabled = false,
  isSearchable,
  position,
  'data-testid': dataTestId = 'dropdownList',
  numberOfSearchResults,
  listRef,
  children,
  dropdownTriggerRef,
  isFloating = false,
  panelRef,
  optionsCount,
  searchEnabledThreshold = 8,
  actionBarProps,
}: PropsWithChildren<DropdownListProps>) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [panelElement, setPanelElement] = useState<HTMLDivElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const shouldShowSearch = isSearchable && optionsCount > searchEnabledThreshold;

  const handleContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      if (panelRef) panelRef.current = node;
      if (isFloating) setPanelElement(node);
    },
    [isFloating, panelRef],
  );

  const calculateMaxHeight = useCallback(() => {
    const referenceElement = dropdownTriggerRef?.current || containerRef.current?.parentElement;
    if (!referenceElement) return;
    const rect = referenceElement.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const availableSpace = position === 'up' ? spaceAbove : spaceBelow;
    const calculatedMaxHeight = Math.max(MIN_HEIGHT, availableSpace - SAFETY_MARGIN);
    setMaxHeight(calculatedMaxHeight);
  }, [position, dropdownTriggerRef]);

  useLayoutEffect(() => {
    if (!isOpen || isFloating) {
      setMaxHeight(undefined);
      return;
    }
    requestAnimationFrame(calculateMaxHeight);
  }, [calculateMaxHeight, isOpen, position, dropdownTriggerRef, isFloating]);

  const { floatingStyle, floatingAttributes, floatingPosition, floatingMaxHeight } = useDropdownFloatingPopper({
    isFloating,
    triggerElement: dropdownTriggerRef?.current ?? null,
    panelElement,
    position,
  });

  if (!isOpen) return;

  const effectivePosition = floatingPosition ?? position;
  const effectiveMaxHeight = isFloating ? floatingMaxHeight : maxHeight;
  const sizeStyle: CSSProperties | undefined = actionBarProps ? undefined : { maxHeight: effectiveMaxHeight };
  const containerStyle: CSSProperties | undefined = isFloating ? { ...floatingStyle, ...sizeStyle } : sizeStyle;

  // The 28rem cap applies to the scrollable list only, so the search input / action bar are excluded from it.
  let scrollAreaMaxHeight = effectiveMaxHeight ? `${effectiveMaxHeight}px` : '28rem';
  if (isFloating && !actionBarProps) {
    scrollAreaMaxHeight = `${Math.min(floatingMaxHeight ?? FLOATING_DEFAULT_MAX_HEIGHT, FLOATING_DEFAULT_MAX_HEIGHT)}px`;
  }

  const dropdownList = (
    <div
      {...(floatingAttributes ?? {})}
      ref={handleContainerRef}
      className={styles.dropdownListContainer}
      data-position={effectivePosition}
      data-floating-dropdown-panel={isFloating || undefined}
      style={containerStyle}
      data-has-action-bar={actionBarProps ? true : undefined}
      data-testid={dataTestId}
    >
      {createPortal(
        <TooltipWrapper
          id={TREE_ITEM_TOOLTIP_ID}
          place="top"
          positionStrategy="fixed"
          style={TOOLTIP_STYLE}
          delayShow={500}
        />,
        document.body,
      )}

      {shouldShowSearch && onSearchValueChange ? (
        <DropdownSearchInput onChange={onSearchValueChange} value={searchValue} />
      ) : null}

      <ScrollArea
        viewportAs="ul"
        as="section"
        type="auto"
        scrollbars="y"
        overscrollBehavior="contain"
        viewportRef={listRef ?? undefined}
        maxHeight={scrollAreaMaxHeight}
        height={isLoading || numberOfSearchResults === 0 ? '10rem' : undefined}
        viewportProps={{
          'className': styles.dropdownList,
          'data-is-loading': isLoading,
          'data-has-no-search-results': numberOfSearchResults === 0,
        }}
      >
        {isLoading ? (
          <LoadingSpinner global={false} hideLoadingMessage={true} />
        ) : !numberOfSearchResults ? (
          <Text>{t('components.dropdownPanel.emptyOptionListHint')}</Text>
        ) : numberOfSearchResults > 0 ? (
          <SelectPanelProvider disabled={panelDisabled} isLoading={isLoading}>
            {children}
          </SelectPanelProvider>
        ) : null}
      </ScrollArea>

      {actionBarProps ? <DropdownActionBar {...actionBarProps} /> : null}
    </div>
  );

  return isFloating ? createPortal(dropdownList, document.body) : dropdownList;
}
